import {PolymerElement, html} from '@polymer/polymer';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';

class KeliBrowseEditRow extends HotkeyBehavior(PolymerElement) {
  static get template() {
    return html`
<style>
  :host {
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    white-space: nowrap;
    box-sizing: border-box;
    background-color: var(--keli-browse-row-edit-background);
    border-top: 1px solid var(--keli-browse-row-focused-border-active);
    border-bottom: 1px solid var(--keli-browse-row-focused-border-active);
    margin-top: -1px; /* Move up the height of the top border */

    --keli-input-field-height: 24px;
    --keli-input-field-width: 100%;
    --keli-input-field-min-height: 24px;
    --keli-input-field-text-align: inherit;
    --keli-input-field-font-size: 15px;
    --keli-input-font-weight: 400;
    --keli-input-letter-spacing: -.5px;
    --keli-input-field-padding: 0;
    --keli-input-field-inner-padding: 0 6px;
    --keli-input-field-border: none;
    --keli-value-min-height: 24px;
    --keli-value-padding: 0;
  }

  ::slotted(.cell) {
    min-width: 150px;
    width: 150px;
    max-width: 150px;
    display: table-cell;
    white-space: nowrap;
    overflow: hidden;
    vertical-align: middle;
    box-sizing: border-box;
    border-left: 1px solid var(--keli-browse-cell-border-left);
    border-right: 1px solid var(--keli-browse-cell-border-right);
  }

  ::slotted(.cell.empty) {
    padding-left: 6px;
    padding-right: 6px;
  }

  ::slotted(.cell:first-of-type) {
    border-left: none;
  }

  ::slotted(.cell.right) {
    text-align: right;
  }

  ::slotted(.cell.left) {
    text-align: left;
  }

  ::slotted(.cell.center) {
    text-align: center;
  }
</style>

<slot></slot>`;
  }

  static get is() {
    return 'keli-browse-edit-row';
  }

  static get properties() {
    return {
      rowHeight: {type: Number, value: 24},
      editing: {type: Boolean, observer: 'editingChanged'},
      row: {type: Object, observer: 'rowChanged'},
      browse: Object,
      targetKeliName: String
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keliFocus', this.handleKeliFocus);
    this.addEventListener('browseCellAttributes', this.handleBrowseCellAttributes);
    this.addEventListener('keliAccepted', this.handleKeliAccepted);
    this.addEventListener('editRowElementAttributeChanged', this.handleEditRowElementAttributeChangedChanged);
    this.addEventListener('keliMultiSelectChanged', this.handleKeliMultiSelectChanged);

    this.AdjacentElement = new AdjacentElement({
      'this': this,
      'selectorNode': this,
      'selector': '.input-field',
      'properties': {disabled: false},
      'wrap': false,
      'defaultCallback': (newElement) => {
        if (newElement && newElement.dispatchEvent) {
          afterNextRender(newElement, () => {
            newElement.dispatchEvent(new CustomEvent('focusElement', {detail: {element: newElement}}));
          })
        }
      }
    });

    this.registerHotkey(KeliInterface.Hotkeys.browse.edit.next, {
      down: () => {
        if (this.AdjacentElement) {
          if (this.AdjacentElement.isLast) {
            this.browse.movePosition(1);
            this.AdjacentElement.first();
          } else {
            this.AdjacentElement.next();
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.edit.previous, {
      down: () => {
        if (this.AdjacentElement) {
          if (this.AdjacentElement.isFirst) {
            this.browse.movePosition(-1);
            this.AdjacentElement.last();
          } else {
            this.AdjacentElement.previous();
          }
        }
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.browse.edit.delete, {
      down: event => {
        event.stopPropagation();
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('browseCellAttributes', this.handleBrowseCellAttributes);
    this.removeEventListener('keliFocus', this.handleKeliFocus);
    this.removeEventListener('keliAccepted', this.handleKeliAccepted);
    this.removeEventListener('editRowElementAttributeChanged', this.handleEditRowElementAttributeChangedChanged);
    this.removeEventListener('keliMultiSelectChanged', this.handleKeliMultiSelectChanged);
    this.browse = undefined;
    this.row = undefined;
  }

  rowChanged(newValue, oldValue) {
    if (!newValue) {
      return;
    }
    this.style.top = `${((newValue.position - 1) * this.rowHeight)}px`;

    for (let cell of this.children) {
      const rowCell = cell.dataset.keliname && newValue.querySelector(`[data-keliname="${cell.dataset.keliname}"]`);
      let cellValue = '';
      try {
        cellValue = this.browse.$.controller.loadedDataIndex[newValue.rowId].cells[cell.dataset.keliname].value;
      } catch (error) {
        cellValue = rowCell && rowCell.textContent;
      }

      this.setContentsFromRowCell(newValue.rowId, rowCell, cell, cellValue, !!oldValue);
    }

    if (this.editing && this.browse && this.browse.focused) {
      this.setFocusOnField();
    }
    if (typeof this.AdjacentElement.element !== 'undefined' && this.AdjacentElement.element.focused && this.AdjacentElement.element.selectOnFocus) {
      this.AdjacentElement.element.$.input.select();
    }
  }

  setContentsFromRowCell(rowId, rowCell, cell, newValue, runBlurAndFocus) {
    if (rowId !== this.row.rowId) {
      return;
    }
    if (!cell) {
      cell = this.querySelector(`[data-keliname="${rowCell.dataset.keliname}"]`);
    }

    if (cell.dataset.multiselect) {
      this.browse?.$.controller.configureMultiSelectToggleElement(cell, rowId, rowCell.firstElementChild?.selected);
      return;
    }

    const field = (cell.firstElementChild && cell.firstElementChild.resetToDefaultValue ? cell.firstElementChild : undefined);
    if (field) {
      field.resetToDefaultValue(newValue, runBlurAndFocus);
      field.boundKeliRowId = rowId;
      field._keliAcceptEventOverrides = {
        rowid: rowId,
        prev: newValue,
        eip: true,
        overriddenscopevars: undefined
      };
    } else {
      cell.innerHTML = rowCell.innerHTML;
    }
  }

  editingChanged(newValue) {
    this.classList.toggle('visible', newValue);
    if (newValue && this.browse && this.browse.focused) {
      this.setFocusOnField();
    }
    if (!newValue) {
      this.AdjacentElement.element = undefined;
      this.row = undefined;
    }
  }

  setFocusOnField() {
    if (this.targetKeliName) {
      const fields = this.AdjacentElement.nodeList;
      for (let i = 0; i < fields.length; ++i) {
        if (fields[i].keliname === this.targetKeliName) {
          this.AdjacentElement.current(fields[i]);
          this.targetKeliName = undefined;
          return;
        }
      }
    }
    this.AdjacentElement.current();
  }

  handleKeliFocus(event) {
    event.stopPropagation();
    this.AdjacentElement.element = event.detail.element;
    this.resetHotkeyState();
  }

  handleBrowseCellAttributes(event) {
    const cell = event.detail.cell || this.querySelector(`[data-keliname="${event.detail.keliname}"]`);
    if (!cell) {
      return;
    }
    if (event.detail.width) {
      cell.style.width = cell.style.maxWidth = cell.style.minWidth = `${event.detail.width}px`;
    }
    if (event.detail.justify) {
      cell.classList.remove('left', 'center', 'right');
      cell.classList.add(event.detail.justify);
    }
  }

  handleKeliAccepted(event) {
    event.stopPropagation();
    if (this.browse) {
      this.browse.$.controller.handleCellChanged({
        rowid: event.detail.rowid,
        field: event.detail.element.keliname,
        value: event.detail.value,
      });
      this.browse.$.controller.setEditableCellValue(event.detail.rowid, event.detail.element, event.detail.value);
    }
  }

  handleEditRowElementAttributeChangedChanged(event) {
    if (!event || !event.detail) {
      return;
    }
    switch (event.detail.attribute) {
      case 'readOnly':
        if (event.detail.element === this.AdjacentElement.currentElement && event.detail.element.readOnly === true) {
          this.AdjacentElement.first();
        }
        break;
    }
  }

  handleKeliMultiSelectChanged(event) {
    event.stopPropagation();

    for (let cell of this.children) {
      if (cell.dataset.multiselect) {
        const rowCell = cell.dataset.keliname && this.row.querySelector(`[data-keliname="${cell.dataset.keliname}"]`);
        if (rowCell) {
          rowCell.firstElementChild.selected = event.detail.state;
          break;
        }
      }
    }
  }
}

customElements.define(KeliBrowseEditRow.is, KeliBrowseEditRow);
export {KeliBrowseEditRow};
